import React, { useEffect, useRef, useState } from "react";

/**
 * AudioLevelMeter — 8-segment LED-style mic level meter.
 *
 * Why
 * ---
 * Hosts have no easy way to tell if their mic is clipping (too loud) or
 * dead-quiet. A real-time meter on the broadcast controls gives them
 * instant feedback to adjust their position before viewers complain.
 *
 * What it shows
 * -------------
 *  - 8 LED segments arranged horizontally
 *  - Green   for -60 to -12 dBFS (segments 1-5)  — healthy signal
 *  - Amber   for -12 to  -3 dBFS (segments 6-7)  — getting hot
 *  - Red     for  -3 to   0 dBFS (segment 8)     — clipping
 *  - A peak-hold tick that decays slowly (~600ms) so transient peaks
 *    stay visible long enough to read
 *
 * Implementation
 * --------------
 * AudioContext analyser node, FFT-free time-domain RMS sampling. Single
 * rAF loop drives the redraw. Shares the global AudioContext with the
 * Announcer FX module so we don't pay the latency penalty of creating
 * a second context.
 */

let _ctx = null;
function ctx() {
  if (!_ctx) _ctx = new (window.AudioContext || window.webkitAudioContext)({ latencyHint: "interactive" });
  if (_ctx.state === "suspended") _ctx.resume().catch(() => {});
  return _ctx;
}

export default function AudioLevelMeter({ stream, on = true, className = "" }) {
  const [segments, setSegments] = useState(0);
  const [peakSegment, setPeakSegment] = useState(0);
  const peakHoldRef = useRef({ value: 0, until: 0 });
  const rafRef = useRef(null);
  const sourceRef = useRef(null);
  const analyserRef = useRef(null);

  useEffect(() => {
    if (!stream || !on) {
      setSegments(0); setPeakSegment(0);
      return;
    }
    const audioTrack = stream.getAudioTracks?.()[0];
    if (!audioTrack || audioTrack.readyState === "ended") return;

    let cancelled = false;
    let ac;
    try {
      ac = ctx();
      const src = ac.createMediaStreamSource(stream);
      const analyser = ac.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.4;
      src.connect(analyser);
      sourceRef.current = src;
      analyserRef.current = analyser;
    } catch {
      return;
    }

    const buf = new Float32Array(analyserRef.current.fftSize);
    const tick = () => {
      if (cancelled) return;
      const a = analyserRef.current;
      if (!a) { rafRef.current = requestAnimationFrame(tick); return; }
      a.getFloatTimeDomainData(buf);
      // Compute RMS
      let sum = 0;
      for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
      const rms = Math.sqrt(sum / buf.length);
      // Convert to dBFS (1.0 = 0 dBFS); clamp -90..0
      const db = rms > 0 ? Math.max(-90, 20 * Math.log10(rms)) : -90;
      // Map -60..0 dB to 0..8 segments
      const seg = Math.max(0, Math.min(8, Math.round(((db + 60) / 60) * 8)));
      setSegments(seg);

      // Peak-hold logic — segment value persists for 600ms then decays.
      const now = performance.now();
      if (seg >= peakHoldRef.current.value) {
        peakHoldRef.current = { value: seg, until: now + 600 };
      } else if (now > peakHoldRef.current.until) {
        peakHoldRef.current.value = Math.max(0, peakHoldRef.current.value - 1);
        peakHoldRef.current.until = now + 80; // single-segment-per-80ms decay
      }
      setPeakSegment(peakHoldRef.current.value);

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      try { sourceRef.current?.disconnect(); } catch {}
      try { analyserRef.current?.disconnect(); } catch {}
      sourceRef.current = null;
      analyserRef.current = null;
    };
  }, [stream, on]);

  // 8 segments — colour by index range
  const colours = [
    "bg-emerald-400", "bg-emerald-400", "bg-emerald-400", "bg-emerald-400", "bg-emerald-400", // 0-4 green
    "bg-amber-400",   "bg-amber-400",   // 5-6 amber
    "bg-rose-500",                     // 7 red
  ];
  const dimColours = [
    "bg-emerald-400/15", "bg-emerald-400/15", "bg-emerald-400/15", "bg-emerald-400/15", "bg-emerald-400/15",
    "bg-amber-400/15",   "bg-amber-400/15",
    "bg-rose-500/15",
  ];

  return (
    <div
      data-testid="audio-level-meter"
      className={`inline-flex items-center gap-[2px] px-1.5 py-1 rounded-md bg-black/40 backdrop-blur-sm border border-white/10 ${className}`}
      title={`Mic level — ${segments} of 8 segments lit`}
    >
      {Array.from({ length: 8 }).map((_, i) => {
        const lit = i < segments;
        const isPeak = peakSegment > 0 && i === peakSegment - 1;
        return (
          <div
            key={i}
            className={`w-[3px] h-3 rounded-sm transition-opacity duration-75 ${
              lit ? colours[i] : isPeak ? colours[i] + " opacity-50" : dimColours[i]
            }`}
          />
        );
      })}
    </div>
  );
}
